import {
  EllipsedText,
  FlexColumn,
  FlexResponsive,
  FlexRow,
  LabeledData,
  MaterialIcon,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Tag } from 'baseui/tag';
import { LabelMedium, LabelSmall, ParagraphSmall } from 'baseui/typography';
import { JOB_TYPE_CATEGORY_LABELS } from '../../../constants/jobTypeCategory';
import { useStyles } from '../../../hooks/useStyles';
import { JobType } from '../../../types/jobs';
import { getTimeBySeconds } from '../../../utils/fields';
import StyledTooltip from '../../ui/StyledTooltip';
import { PdfSettingsModal } from './PdfSettingsModal';

type Props = {
  jobType: JobType;
};
export function JobTypeResume({ jobType }: Readonly<Props>) {
  const { css, classes, theme } = useStyles();
  const { showModal, openModal, closeModal } = useModal();

  const labelWidth = '130px';
  const labelXLWidth = '220px';
  const contentMinWidth = '280px';

  const { pdfSettings } = jobType;

  return (
    <FlexResponsive>
      <FlexColumn classNames={css({ flex: '70%' })}>
        <LabelMedium className={classes.dataHeaderClass}>
          <FlexRow classNames={css({ minHeight: '28px', alignItems: 'center' })}>Tipo de trabajo</FlexRow>
        </LabelMedium>
        <FlexResponsive
          classNames={css({ padding: `0px ${theme.sizing.scale400}` })}
          breakAt="medium"
        >
          <FlexColumn classNames={css({ flex: '40%', minWidth: '300px' })}>
            <FlexColumn gap={theme.sizing.scale300}>
              <LabeledData
                label={'Nombre:'}
                content={
                  <ParagraphSmall margin={0}>
                    <EllipsedText maxLines={0}>{jobType.name || '-'}</EllipsedText>
                  </ParagraphSmall>
                }
                labelWidth={labelWidth}
                minWidth={contentMinWidth}
              />
              <LabeledData
                label={'Descripción:'}
                content={
                  <ParagraphSmall margin={0}>
                    <EllipsedText maxLines={0}>{jobType.description || '-'}</EllipsedText>
                  </ParagraphSmall>
                }
                labelWidth={labelWidth}
                minWidth={contentMinWidth}
              />
              <LabeledData
                label={'Costo:'}
                content={jobType.cost ? `$${Intl.NumberFormat().format(jobType.cost)}` : '-'}
                labelWidth={labelWidth}
                minWidth={contentMinWidth}
              />
              <LabeledData
                label={'Duración promedio:'}
                content={jobType.avgDuration ? getTimeBySeconds(jobType.avgDuration) : '-'}
                labelWidth={labelWidth}
                minWidth={contentMinWidth}
              />
              <LabeledData
                label="Categoría:"
                content={JOB_TYPE_CATEGORY_LABELS[jobType.category] ?? '-'}
                labelWidth={labelWidth}
                minWidth={contentMinWidth}
              />
            </FlexColumn>
          </FlexColumn>
          <FlexColumn
            gap={theme.sizing.scale300}
            classNames={css({ flex: '60%' })}
          >
            <FlexColumn gap={0}>
              <LabelSmall>Compañias:</LabelSmall>
              <FlexRow
                gap={0}
                classNames={css({ flexWrap: 'wrap' })}
              >
                {jobType.tenants.map((tenant) => (
                  <Tag
                    closeable={false}
                    key={tenant._id}
                  >
                    {tenant.name}
                  </Tag>
                ))}
              </FlexRow>
            </FlexColumn>
            <FlexColumn gap={0}>
              <LabelSmall>Habilidades:</LabelSmall>
              <FlexRow
                gap={0}
                classNames={css({ flexWrap: 'wrap' })}
              >
                {jobType.technicianSkills.map((skill) => (
                  <Tag
                    closeable={false}
                    key={skill.slug}
                  >
                    {skill.name}
                  </Tag>
                ))}
              </FlexRow>
            </FlexColumn>
          </FlexColumn>
        </FlexResponsive>
      </FlexColumn>
      <FlexColumn classNames={css({ flex: '30%' })}>
        <LabelMedium className={classes.dataHeaderClass}>
          <FlexRow classNames={css({ justifyContent: 'space-between', alignItems: 'center' })}>
            Configuración del PDF
            <StyledTooltip
              content={'Editar'}
              showArrow={false}
            >
              <Button
                size="mini"
                shape="square"
                onClick={openModal}
                kind="tertiary"
              >
                <MaterialIcon
                  name={'edit'}
                  size="xs-mini"
                />
              </Button>
            </StyledTooltip>
          </FlexRow>
        </LabelMedium>
        <FlexColumn
          gap={theme.sizing.scale300}
          classNames={css({ padding: `0px ${theme.sizing.scale400}` })}
        >
          <LabeledData
            label={'Titulo:'}
            content={
              <ParagraphSmall margin={0}>
                <EllipsedText maxLines={0}>{pdfSettings.title ?? '-'}</EllipsedText>
              </ParagraphSmall>
            }
            labelWidth={'60px'}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Permitir exportación de PDF:'}
            content={pdfSettings.allowPDFExport ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Mostrar datos del vehículo:'}
            content={pdfSettings.showVehicleData ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Mostrar datos del cliente:'}
            content={pdfSettings.showClientData ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Mostrar logotipo de la compañia:'}
            content={pdfSettings.showTenantLogo ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Mostrar linea del tiempo:'}
            content={pdfSettings.showTimeline ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
          <LabeledData
            label={'Mostrar lugar:'}
            content={pdfSettings.showPlace ? 'Si' : 'No'}
            labelWidth={labelXLWidth}
            minWidth={contentMinWidth}
          />
        </FlexColumn>
      </FlexColumn>
      <PdfSettingsModal
        jobTypeId={jobType._id}
        pdfSettings={pdfSettings}
        isOpen={showModal}
        onClose={closeModal}
      />
    </FlexResponsive>
  );
}
